// src/pages/BetaSignups.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import { StatusBadge } from '../components/Badge';
import {
    EyeIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    GlobeAltIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const BetaSignups = () => {
    const { executeQuery, queries, loading, error } = useGraphQL();
    const [signups, setSignups] = useState([]);
    const [selectedSignup, setSelectedSignup] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [filters, setFilters] = useState({
        companySize: '',
        country: '',
        industry: '',
        search: ''
    });
    const [stats, setStats] = useState({
        total: 0,
        thisMonth: 0,
        companies: 0,
        countries: 0
    });

    useEffect(() => {
        loadSignups();
    }, []);

    useEffect(() => {
        updateStats();
    }, [signups]);

    const loadSignups = async () => {
        try {
            const data = await executeQuery(queries.getBetaSignups(), {
                limit: 1000, // Get all signups for analysis
                offset: 0
            });
            setSignups(data.betaSignups || []);
        } catch (err) {
            console.error('Failed to load beta signups:', err);
        }
    };

    const updateStats = () => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const thisMonthSignups = signups.filter(signup => {
            const signupDate = new Date(signup.createdAt);
            return signupDate.getMonth() === currentMonth && signupDate.getFullYear() === currentYear;
        });

        const uniqueCompanies = new Set(
            signups
                .filter(s => s.companyName)
                .map(s => s.companyName.toLowerCase())
        ).size;

        const uniqueCountries = new Set(
            signups
                .filter(s => s.country)
                .map(s => s.country)
        ).size;

        setStats({
            total: signups.length,
            thisMonth: thisMonthSignups.length,
            companies: uniqueCompanies,
            countries: uniqueCountries
        });
    };

    const openDetailModal = (signup) => {
        setSelectedSignup(signup);
        setShowDetailModal(true);
    };

    const exportToCSV = () => {
        const headers = [
            'Created At',
            'First Name',
            'Last Name',
            'Email',
            'Work Email',
            'Company Name',
            'Company Size',
            'Company Industry',
            'Job Title',
            'Country',
            'Annual Tender Volume',
            'Interests',
            'LinkedIn Profile',
            'Additional Info',
            'Accept Terms'
        ];

        const rows = filteredSignups.map(signup => [
            new Date(signup.createdAt).toLocaleDateString(),
            signup.firstName || '',
            signup.lastName || '',
            signup.email || '',
            signup.workEmail || '',
            signup.companyName || '',
            signup.companySize || '',
            signup.companyIndustry || '',
            signup.jobTitle || '',
            signup.country || '',
            signup.annualTenderVolume || '',
            signup.interests ? signup.interests.join(', ') : '',
            signup.linkedInProfile || '',
            signup.additionalInfo || '',
            signup.acceptTerms ? 'Yes' : 'No'
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `beta-signups-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    // Filter signups based on current filters
    const filteredSignups = signups.filter(signup => {
        const matchesSearch = !filters.search ||
            signup.firstName?.toLowerCase().includes(filters.search.toLowerCase()) ||
            signup.lastName?.toLowerCase().includes(filters.search.toLowerCase()) ||
            signup.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
            signup.companyName?.toLowerCase().includes(filters.search.toLowerCase());

        const matchesCompanySize = !filters.companySize || signup.companySize === filters.companySize;
        const matchesCountry = !filters.country || signup.country === filters.country;
        const matchesIndustry = !filters.industry || signup.companyIndustry === filters.industry;

        return matchesSearch && matchesCompanySize && matchesCountry && matchesIndustry;
    });

    // Get unique values for filters
    const uniqueCompanySizes = [...new Set(signups.map(s => s.companySize).filter(Boolean))].sort();
    const uniqueCountries = [...new Set(signups.map(s => s.country).filter(Boolean))].sort();
    const uniqueIndustries = [...new Set(signups.map(s => s.companyIndustry).filter(Boolean))].sort();

    const columns = [
        {
            key: 'name',
            title: 'Name',
            sortable: true,
            render: (_, signup) => (
                <div>
                    <div className="font-medium text-white">
                        {signup.firstName} {signup.lastName}
                    </div>
                    <div className="text-sm text-gray-400">{signup.email}</div>
                </div>
            )
        },
        {
            key: 'company',
            title: 'Company',
            render: (_, signup) => (
                <div>
                    <div className="text-white font-medium">
                        {signup.companyName || 'Not specified'}
                    </div>
                    <div className="text-sm text-gray-400">
                        {signup.jobTitle && <span>{signup.jobTitle}</span>}
                        {signup.jobTitle && signup.companySize && <span> • </span>}
                        {signup.companySize && <span>{signup.companySize}</span>}
                    </div>
                </div>
            )
        },
        {
            key: 'industry',
            title: 'Industry',
            render: (_, signup) => signup.companyIndustry || <span className="text-gray-400">-</span>
        },
        {
            key: 'location',
            title: 'Location',
            render: (_, signup) => signup.country || <span className="text-gray-400">-</span>
        },
        {
            key: 'tenderVolume',
            title: 'Tender Volume',
            render: (_, signup) => signup.annualTenderVolume ? (
                <StatusBadge status={signup.annualTenderVolume.replace(/[^a-zA-Z]/g, '').toLowerCase()} />
            ) : (
                <span className="text-gray-400">-</span>
            )
        },
        {
            key: 'interests',
            title: 'Interests',
            render: (_, signup) => signup.interests && signup.interests.length > 0 ? (
                <div className="text-sm">
                    <span className="text-white">{signup.interests.length} selected</span>
                    <div className="text-xs text-gray-400 truncate max-w-xs">
                        {signup.interests.slice(0, 2).join(', ')}
                        {signup.interests.length > 2 && '...'}
                    </div>
                </div>
            ) : (
                <span className="text-gray-400">None</span>
            )
        },
        {
            key: 'createdAt',
            title: 'Signed Up',
            sortable: true,
            render: (date) => (
                <div>
                    <div className="text-white">{new Date(date).toLocaleDateString()}</div>
                    <div className="text-sm text-gray-400">{new Date(date).toLocaleTimeString()}</div>
                </div>
            )
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, signup) => (
                <div className="flex space-x-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openDetailModal(signup);
                        }}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="View details"
                    >
                        <EyeIcon className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Beta Signups</h1>
                <div className="flex space-x-3">
                    <button
                        onClick={exportToCSV}
                        className="px-4 py-2 bg-accent-green text-white hover:bg-opacity-90 flex items-center space-x-2"
                    >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        <span>Export CSV</span>
                    </button>
                    <button
                        onClick={loadSignups}
                        className="px-4 py-2 bg-primary text-white hover:bg-opacity-90"
                        disabled={loading}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-primary p-2 mr-3">
                            <UserGroupIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Total Signups</p>
                            <p className="text-xl font-semibold text-white">{stats.total}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-accent-green p-2 mr-3">
                            <UserGroupIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">This Month</p>
                            <p className="text-xl font-semibold text-white">{stats.thisMonth}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-accent-blue p-2 mr-3">
                            <BuildingOfficeIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Companies</p>
                            <p className="text-xl font-semibold text-white">{stats.companies}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-secondary p-2 mr-3">
                            <GlobeAltIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Countries</p>
                            <p className="text-xl font-semibold text-white">{stats.countries}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-gray-800 p-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Search</label>
                        <input
                            type="text"
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            placeholder="Name, email, company..."
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Company Size</label>
                        <select
                            value={filters.companySize}
                            onChange={(e) => setFilters(prev => ({ ...prev, companySize: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Sizes</option>
                            {uniqueCompanySizes.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Country</label>
                        <select
                            value={filters.country}
                            onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Countries</option>
                            {uniqueCountries.map(country => (
                                <option key={country} value={country}>{country}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Industry</label>
                        <select
                            value={filters.industry}
                            onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Industries</option>
                            {uniqueIndustries.map(industry => (
                                <option key={industry} value={industry}>{industry}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => setFilters({ companySize: '', country: '', industry: '', search: '' })}
                            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                <div className="mt-3 text-sm text-gray-400">
                    Showing {filteredSignups.length} of {signups.length} signups
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Signups Table */}
            <DataTable
                columns={columns}
                data={filteredSignups}
                loading={loading}
                onRowClick={(signup) => openDetailModal(signup)}
            />

            {/* Signup Detail Modal */}
            <Modal
                isOpen={showDetailModal}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedSignup(null);
                }}
                title="Beta Signup Details"
                size="lg"
            >
                {selectedSignup && <SignupDetail signup={selectedSignup} />}
            </Modal>
        </div>
    );
};

// Signup Detail Component
const SignupDetail = ({ signup }) => {
    return (
        <div className="space-y-6">
            {/* Personal Information */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-3">Personal Information</h4>
                <div className="bg-gray-700 p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-gray-400">Name:</span>
                            <span className="text-white ml-2">{signup.firstName} {signup.lastName}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Email:</span>
                            <span className="text-white ml-2">{signup.email}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Work Email:</span>
                            <span className="text-white ml-2">{signup.workEmail || 'Not provided'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">LinkedIn:</span>
                            {signup.linkedInProfile ? (
                                <a
                                    href={signup.linkedInProfile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary ml-2 hover:underline"
                                >
                                    View Profile
                                </a>
                            ) : (
                                <span className="text-white ml-2">Not provided</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Company Information */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-3">Company Information</h4>
                <div className="bg-gray-700 p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-gray-400">Company:</span>
                            <span className="text-white ml-2">{signup.companyName || 'Not provided'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Size:</span>
                            <span className="text-white ml-2">{signup.companySize || 'Not provided'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Industry:</span>
                            <span className="text-white ml-2">{signup.companyIndustry || 'Not provided'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Job Title:</span>
                            <span className="text-white ml-2">{signup.jobTitle || 'Not provided'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Country:</span>
                            <span className="text-white ml-2">{signup.country || 'Not provided'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Business Details */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-3">Business Details</h4>
                <div className="bg-gray-700 p-4">
                    <div className="grid grid-cols-1 gap-4 text-sm">
                        <div>
                            <span className="text-gray-400">Annual Tender Volume:</span>
                            <span className="text-white ml-2">{signup.annualTenderVolume || 'Not provided'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Interests:</span>
                            <div className="mt-1">
                                {signup.interests && signup.interests.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {signup.interests.map((interest, index) => (
                                            <span
                                                key={index}
                                                className="px-2 py-1 bg-primary bg-opacity-20 text-primary rounded-full text-xs"
                                            >
                                                {interest}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-white">None specified</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Information */}
            {signup.additionalInfo && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-3">Additional Information</h4>
                    <div className="bg-gray-700 p-4">
                        <p className="text-white text-sm whitespace-pre-wrap">{signup.additionalInfo}</p>
                    </div>
                </div>
            )}

            {/* Metadata */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-3">Signup Details</h4>
                <div className="bg-gray-700 p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-gray-400">Signed Up:</span>
                            <span className="text-white ml-2">{new Date(signup.createdAt).toLocaleString()}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Terms Accepted:</span>
                            <span className={`ml-2 ${signup.acceptTerms ? 'text-green-700' : 'text-red-700'}`}>
                                {signup.acceptTerms ? 'Yes' : 'No'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">Last Updated:</span>
                            <span className="text-white ml-2">{new Date(signup.updatedAt).toLocaleString()}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BetaSignups;